import { motion } from 'framer-motion'
import { X, Share, Plus } from 'lucide-react'

interface IOSInstallGuideProps {
  /** Backdrop tap and the X button. */
  onClose: () => void
  /** The "Entendi" button; falls back to onClose. */
  onConfirm?: () => void
}

/**
 * The three steps to add BT Vision to the iOS home screen.
 *
 * iOS gives web apps nothing on tap-to-install and shows no prompt of its own,
 * so this walkthrough is the install funnel on that platform — and, since iOS
 * grants the Push API to installed home-screen apps only, it is also the way
 * into notifications there. Shared by the install banner and the Settings
 * notifications card; wrap it in <AnimatePresence> to get the exit animation.
 */
export function IOSInstallGuide({ onClose, onConfirm }: IOSInstallGuideProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm glass-card rounded-2xl p-6"
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-display text-lg font-bold text-white">
            Instalar no iPhone/iPad
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-sm font-bold text-turquoise">1</div>
            <div>
              <p className="text-white text-sm font-medium">Toque em Compartilhar</p>
              <p className="text-white/50 text-xs mt-0.5 flex items-center gap-1">
                O ícone <Share className="w-3.5 h-3.5 inline" /> na barra do navegador
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-sm font-bold text-turquoise">2</div>
            <div>
              <p className="text-white text-sm font-medium">Adicionar à Tela de Início</p>
              <p className="text-white/50 text-xs mt-0.5 flex items-center gap-1">
                Toque em <Plus className="w-3.5 h-3.5 inline" /> Adicionar à Tela de Início
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-sm font-bold text-turquoise">3</div>
            <div>
              <p className="text-white text-sm font-medium">Confirmar</p>
              <p className="text-white/50 text-xs mt-0.5">
                Toque em "Adicionar" no canto superior direito
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={onConfirm ?? onClose}
          className="mt-5 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white/70 text-sm transition-colors"
        >
          Entendi
        </button>
      </motion.div>
    </motion.div>
  )
}
